import { FarmRiskType } from '@ourbranch/lookups';

import { wholeDollarCoverageCMinimum } from './migrated-limit-approvals';

/**
 * Coverage C business rules shape — mirrors `packages/brand-config-be/src/types.ts`
 * (`FarmRiskTypeConfig.businessRules.coverageC`). Defined locally so farm-utils
 * stays decoupled from brand-config-be.
 */
export type FarmCoverageCRules = {
  min?: number;
  minPctOfCoverageA?: number;
  max: number | 'coverageA';
  // Flat dollar SEED for the field (#33264), independent of the floor (`min`). Lets the default
  // diverge from the floor: CFM's ACV Tenant is `{ min: 0, max: 25000, defaultValue: 5000 }` — the
  // agent can decrease to $0 (min) but a fresh field seeds at $5,000. Ignored when `minPctOfCoverageA`
  // is set (a percent-of-Coverage-A rule always seeds off Coverage A). Mirrors Coverage D's defaultValue.
  defaultValue?: number;
};

export type FarmCoverageCBusinessRules = {
  byResidenceUsage?: Record<string, FarmCoverageCRules>;
  byLossSettlement?: Record<
    string,
    {
      byResidenceUsage: Record<string, FarmCoverageCRules>;
    }
  >;
};

/**
 * Resolves the Coverage C rule entry for a given residence usage / loss settlement pair.
 *
 * FO2 defines rules via `byLossSettlement[…].byResidenceUsage[…]`; FO3 / FO1 use
 * `byResidenceUsage[…]` directly. We try the nested lookup first and fall through
 * to the flat one, matching the pattern already used in
 * `packages/underwriting-rules/.../farm-post-data-rules.ts` and
 * `staff-fe/.../farm-shared.validation-schema.js`.
 */
export function resolveFarmCoverageCRules({
  residenceUsage,
  lossSettlement,
  businessRulesC
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  businessRulesC?: FarmCoverageCBusinessRules;
}): FarmCoverageCRules | undefined {
  if (!businessRulesC) {
    return undefined;
  }

  if (businessRulesC.byLossSettlement && lossSettlement && residenceUsage) {
    const rules = businessRulesC.byLossSettlement[lossSettlement]?.byResidenceUsage?.[residenceUsage];
    if (rules) {
      return rules;
    }
  }

  if (businessRulesC.byResidenceUsage && residenceUsage) {
    return businessRulesC.byResidenceUsage[residenceUsage];
  }

  return undefined;
}

/**
 * Computes the default Coverage C value for a dwelling risk.
 *
 * Context: GitHub issue #29084 — Forreston FO2/FO3 tenant-occupied risks were
 * defaulting to 50% / 75% of Coverage A. Those defaults then got clamped by
 * post-data-rules to the tenant max ($25,000), so risks appeared to "default to
 * the max." The correct default for a tenant rule (`{ min: 5000, max: 25000 }`)
 * is the configured minimum ($5,000).
 *
 * Resolution order:
 *   1. `minPctOfCoverageA` rule → `coverageA * minPctOfCoverageA`, rounded up to the whole dollar
 *      (e.g., FO3 Primary = 75% of CovA).
 *   2. Explicit `defaultValue` (#33264) → that amount. Lets the SEED default diverge from the floor
 *      (`min`): CFM's ACV Tenant floors at $0 but seeds at $5,000.
 *   3. Fixed-range rule (numeric `min` + numeric `max`) → `min`. This is the
 *      tenant case with no explicit seed: start users at the floor, not the ceiling.
 *   4. No matching rule → legacy fallback (50% FO2, 75% FO3) to preserve
 *      behavior for carriers/risk-types without configured rules.
 */
export function getDefaultFarmCoverageC({
  riskType,
  residenceUsage,
  lossSettlement,
  coverageA,
  businessRulesC
}: {
  riskType: string;
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  coverageA: number;
  businessRulesC?: FarmCoverageCBusinessRules;
}): number {
  const rules = resolveFarmCoverageCRules({ residenceUsage, lossSettlement, businessRulesC });

  // #37109: a percent-of-A default IS the rule's minimum, so it is rounded UP like the floor: rounding
  // to the nearest dollar seeded A $200,003 × 75% = $150,002.25 at $150,002, under the rule.
  if (rules?.minPctOfCoverageA != null) {
    return wholeDollarCoverageCMinimum(coverageA, rules.minPctOfCoverageA);
  }

  if (rules?.defaultValue != null) {
    return rules.defaultValue;
  }

  if (rules && typeof rules.max === 'number' && rules.min != null) {
    return rules.min;
  }

  // Legacy fallback — carriers/risk-types with no Coverage C rule configured.
  if (riskType === FarmRiskType.Fo3) {
    return wholeDollarCoverageCMinimum(coverageA, 0.75);
  }
  if (riskType === FarmRiskType.Fo2) {
    return wholeDollarCoverageCMinimum(coverageA, 0.5);
  }

  return 0;
}

/**
 * Computes the MINIMUM (floor) Coverage C value the agent may reduce to — as distinct from the
 * SEED default (`getDefaultFarmCoverageC`, the value a fresh field starts at).
 *
 * Context: GitHub issue #33150. The FO2 Coverage C "keep at/above the floor" effect (added in
 * #31820 to rescue a stale seed after a Coverage A valuation bump) originally clamped up to the
 * SEED default. For an ACV Primary/Seasonal rule (`{ min: 0, max: 'coverageA' }`) that seed is 50%
 * of Coverage A (the legacy fallback), so the effect stomped any lower value the agent typed —
 * they could never decrease Coverage C to $0, and partial keystrokes were clamped mid-typing
 * ("acting wonky when I try to increase"). Clamping to this true floor fixes both: ACV P/S floor is
 * $0, while RC P/S keeps its 50%-of-Coverage-A floor (preserving #31820) and tenant keeps its
 * $5,000 floor.
 *
 * Mirrors the min logic in `farm-shared.validation-schema.js`:
 * `minPctOfCoverageA ? coverageA * minPctOfCoverageA : (min ?? 0)`, as the smallest whole dollar it
 * accepts (#37109).
 */
export function getFarmCoverageCMinimum({
  residenceUsage,
  lossSettlement,
  coverageA,
  businessRulesC
}: {
  residenceUsage?: string | null;
  lossSettlement?: string | null;
  coverageA?: number | null;
  businessRulesC?: FarmCoverageCBusinessRules;
}): number {
  const rules = resolveFarmCoverageCRules({ residenceUsage, lossSettlement, businessRulesC });
  if (!rules) {
    return 0;
  }
  if (rules.minPctOfCoverageA != null) {
    // #37109: rounded UP, so the floor is never under the unrounded `A × pct` the validator checks
    // (rounding to the nearest dollar put A $200,003 × 75% = $150,002.25 at $150,002).
    return wholeDollarCoverageCMinimum(coverageA ?? 0, rules.minPctOfCoverageA);
  }
  return rules.min ?? 0;
}
